// Price Book — every craft, CAT-CLASS and part we have ever priced, what it
// was quoted at, and whether we WON with it.
//
// Admin/leadership only (same gate as Estimating, enforced server-side in
// routes/price-history.js — this nav gate is a mirror, not the boundary).
// Bill side only: no cost, burden or margin is fetched or shown.

import { useEffect, useMemo, useState } from 'react';
import { api } from '../api';
import { styles, colors, fmtDate } from '../components/UI';
import { PriceHistoryModal, OutcomeChip, money } from '../components/PriceHistory';
import { useTableControls, SortableFilterTh, ActiveFiltersBar } from '../components/TableControls';
import { csvSafeCell } from '../utils/csvSafe';

const ORANGE = colors?.orange || '#E36F2C';
const KINDS = [
  { key: '', label: 'Everything' },
  { key: 'labor', label: '👷 Labor' },
  { key: 'equipment', label: '🔧 Equipment' },
  { key: 'consumable', label: '📦 Consumables & Services' },
];

export default function PriceBook() {
  const [rows, setRows] = useState([]);
  const [kind, setKind] = useState('');
  const [q, setQ] = useState('');
  const [customers, setCustomers] = useState([]);
  const [customerId, setCustomerId] = useState('');
  const [wonOnly, setWonOnly] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [open, setOpen] = useState(null);   // { kind, key, label }

  useEffect(() => {
    // Customer filter list. Non-fatal: the book works fleet-wide without it.
    api.listCustomers({}).then(r => setCustomers(Array.isArray(r) ? r : (r?.rows || []))).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    api.priceBook({ kind: kind || null, q: q.trim() || null, customer_id: customerId || null })
      .then(r => { setRows(r || []); setError(''); })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [kind, q, customerId]);

  const visible = useMemo(
    () => (wonOnly ? rows.filter(r => r.won?.won_count > 0) : rows),
    [rows, wonOnly]
  );

  const totals = useMemo(() => {
    const won = rows.reduce((n, r) => n + (r.won?.won_count || 0), 0);
    const decided = rows.reduce((n, r) => n + (r.won?.decided_count || 0), 0);
    return { items: rows.length, won, decided, rate: decided ? Math.round((won / decided) * 100) : null };
  }, [rows]);

  // CSV of exactly what is on screen, in the order it is on screen.
  const exportCsv = (list) => {
    const head = ['Kind', 'Item', 'CAT-CLASS / Part', 'Quotes', 'Low', 'Median', 'High',
      'Won', 'Decided', 'Win %', 'Won low', 'Won high', 'Last rate', 'Last customer', 'Last quoted', 'Jobs', 'Customers'];
    // csvSafeCell first (formula-injection guard on customer/item text), then quote.
    const esc = (v) => `"${String(csvSafeCell(v == null ? '' : v)).replace(/"/g, '""')}"`;
    const body = list.map(r => [
      r.kind, r.label, r.cat_class || r.part_number || '',
      r.count, r.min, r.median, r.max,
      r.won?.won_count ?? 0, r.won?.decided_count ?? 0, r.won?.win_rate ?? '',
      r.won?.min ?? '', r.won?.max ?? '',
      r.last?.rate ?? '', r.last?.customer_name ?? '', r.last?.quoted_at ? String(r.last.quoted_at).slice(0, 10) : '',
      r.jobs, r.customers,
    ].map(esc).join(','));
    const csv = String.fromCharCode(0xFEFF) + [head.map(esc).join(','), ...body].join('\r\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `sss-price-book-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const accessors = {
    label: r => r.label,
    cat: r => r.cat_class || r.part_number || '',
    count: r => r.count,
    min: r => r.min,
    median: r => r.median,
    max: r => r.max,
    win: r => (r.won?.win_rate == null ? -1 : r.won.win_rate),
    wonband: r => (r.won?.median ?? -1),
    last: r => (r.last?.quoted_at ? new Date(r.last.quoted_at).getTime() : 0),
  };
  const tc = useTableControls(visible, accessors);
  const shown = tc.displayRows;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginBottom: 10 }}>
        <h1 style={styles.h1}>📈 Price Book</h1>
        <button style={styles.btnSecondary} onClick={() => exportCsv(shown)} disabled={!shown.length}>
          ⬇ Export CSV ({shown.length})
        </button>
      </div>

      <div style={{ ...styles.card, marginBottom: 12 }}>
        <div style={{ fontSize: 12, color: '#6B7280', marginBottom: 8 }}>
          Every rate we have quoted, from the current revision of each estimate — one job counts once
          however many lines it was split across. <strong>Won</strong> means the opportunity is Won,
          Active or Complete, or its quote was Accepted; open bids are not counted as losses.
          Equipment rates carry their billing basis and tier, because a Utilization rate and a
          Non-Consignment rate are not the same number.
        </div>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
          {KINDS.map(k => (
            <button key={k.key} onClick={() => setKind(k.key)}
              style={{
                ...styles.btnSecondary, fontSize: 12,
                background: kind === k.key ? ORANGE : '#fff',
                color: kind === k.key ? '#fff' : '#374151',
                border: `1px solid ${kind === k.key ? ORANGE : '#D1D5DB'}`,
              }}>{k.label}</button>
          ))}
          <input style={{ ...styles.input, flex: '1 1 200px', minWidth: 160, fontSize: 12 }}
            placeholder="Search item, CAT-CLASS or part…" value={q} onChange={e => setQ(e.target.value)} />
          <select style={{ ...styles.input, fontSize: 12, maxWidth: 240 }}
            value={customerId} onChange={e => setCustomerId(e.target.value)}>
            <option value="">Any customer</option>
            {customers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <label style={{ fontSize: 12, color: '#374151', display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
            <input type="checkbox" checked={wonOnly} onChange={e => setWonOnly(e.target.checked)} />
            Won only
          </label>
        </div>

        <div style={{ marginTop: 8, fontSize: 12, color: '#6B7280' }}>
          {loading ? 'Loading…' : (
            <>
              <strong style={{ color: '#1F2937' }}>{totals.items}</strong> priced items
              {totals.decided > 0 && <> · <strong style={{ color: '#065F46' }}>{totals.won}</strong> of {totals.decided} decided quotes won ({totals.rate}%)</>}
              {shown.length !== visible.length && <> · showing {shown.length}</>}
            </>
          )}
        </div>
        {error && <div style={{ color: '#B91C1C', fontSize: 12, marginTop: 6 }}>{error}</div>}
      </div>

      <div style={styles.card}>
        <ActiveFiltersBar filters={tc.filters} clearFilter={tc.clearFilter} clearAll={tc.clearAll}
          colLabels={{ label: 'Item', cat: 'CAT-CLASS / Part', count: 'Quotes' }} />
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr style={{ background: '#1F2937', color: '#fff' }}>
                <SortableFilterTh {...tc.headerProps('label')} label="Item" />
                <SortableFilterTh {...tc.headerProps('cat')} label="CAT-CLASS / Part" />
                <SortableFilterTh {...tc.headerProps('count')} label="Quotes" align="right" />
                <SortableFilterTh {...tc.headerProps('min')} label="Low" align="right" noFilter />
                <SortableFilterTh {...tc.headerProps('median')} label="Median" align="right" noFilter />
                <SortableFilterTh {...tc.headerProps('max')} label="High" align="right" noFilter />
                <SortableFilterTh {...tc.headerProps('wonband')} label="Won at" align="right" noFilter />
                <SortableFilterTh {...tc.headerProps('win')} label="Win %" align="right" noFilter />
                <SortableFilterTh {...tc.headerProps('last')} label="Last quoted" noFilter />
              </tr>
            </thead>
            <tbody>
              {shown.map(r => (
                <tr key={`${r.kind}:${r.key}`} style={{ borderBottom: '1px solid #F3F4F6', cursor: 'pointer' }}
                  onClick={() => setOpen({ kind: r.kind, key: r.key, label: r.label })}>
                  <td style={{ padding: '5px 7px' }}>
                    <div style={{ fontWeight: 600, color: ORANGE }}>{r.label}</div>
                    <div style={{ fontSize: 10, color: '#9CA3AF' }}>
                      {r.kind}{r.category ? ` · ${r.category}` : ''} · {r.jobs} job{r.jobs === 1 ? '' : 's'} · {r.customers} customer{r.customers === 1 ? '' : 's'}
                    </div>
                  </td>
                  <td style={cell}>{r.cat_class || r.part_number || '—'}</td>
                  <td style={cell}>{r.count}</td>
                  <td style={cell}>{money(r.min)}</td>
                  <td style={{ ...cell, fontWeight: 700, color: '#1F2937' }}>{money(r.median)}</td>
                  <td style={cell}>{money(r.max)}</td>
                  <td style={cell}>
                    {r.won?.won_count
                      ? <span style={{ color: '#065F46', fontWeight: 700 }}>
                          {r.won.min === r.won.max ? money(r.won.min) : `${money(r.won.min)}–${money(r.won.max)}`}
                        </span>
                      : <span style={{ color: '#9CA3AF' }}>—</span>}
                  </td>
                  <td style={cell}>
                    {r.won?.win_rate == null
                      ? <span style={{ color: '#9CA3AF' }} title="No decided quotes yet — still open">—</span>
                      : <>
                          <strong style={{ color: r.won.win_rate >= 50 ? '#065F46' : '#B91C1C' }}>{r.won.win_rate}%</strong>
                          <span style={{ color: '#9CA3AF', fontSize: 10 }}> {r.won.won_count}/{r.won.decided_count}</span>
                        </>}
                  </td>
                  <td style={cell}>
                    {r.last ? (
                      <>
                        <div>{money(r.last.rate)} <OutcomeChip outcome={r.last.outcome} /></div>
                        <div style={{ fontSize: 10, color: '#9CA3AF' }}>
                          {r.last.customer_name || '—'} · {fmtDate(r.last.quoted_at) || '—'}
                        </div>
                      </>
                    ) : '—'}
                  </td>
                </tr>
              ))}
              {!loading && !shown.length && (
                <tr><td colSpan={9} style={{ padding: 14, color: '#6B7280', fontSize: 12 }}>
                  Nothing priced matches this filter.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {open && (
        <PriceHistoryModal
          kind={open.kind} itemKey={open.key} label={open.label}
          customerId={customerId ? Number(customerId) : null}
          onClose={() => setOpen(null)}
        />
      )}
    </div>
  );
}

const cell = { padding: '5px 7px', color: '#374151', whiteSpace: 'nowrap' };
